import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import type { SearchResult } from '@fint/validation';
import { useSwitchOrg } from '@/hooks/useSwitchOrg';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/cn';
import { SearchResultsList } from './SearchResultsList';
import { useGlobalSearch } from './useGlobalSearch';

/**
 * The search bar across the top of every page, from a tablet up - the box
 * itself, typed into where it is, the way a search engine's is.
 *
 * It replaced a "Search…" button in the sidebar that opened a dialog: the
 * owner asked for a search bar, and a button that looks like one is a step
 * between a person and the thing they came to do.
 *
 * ── HOW IT ANSWERS ──
 * Suggestions drop down under it as you type (six of each kind). Arrow keys
 * walk them and Enter opens the one picked; Enter WITHOUT picking one goes to
 * the results page, /search?q=, which lists every match - the same split a
 * search engine makes between its suggestions and its results. Esc closes the
 * suggestions; "/" or Ctrl+K puts the cursor in the bar from anywhere.
 *
 * A phone has no room for it and keeps its search icon and full-screen sheet
 * (SearchPanel).
 */
export function SearchBar() {
  const navigate = useNavigate();
  const switchOrg = useSwitchOrg();
  const activeOrgId = useAuthStore((st) => st.activeOrgId);
  const s = useGlobalSearch(activeOrgId);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  /** True once an arrow key has picked a suggestion: then Enter opens it. */
  const [picked, setPicked] = useState(false);

  // "/" (when not already typing somewhere) and Ctrl+K focus the bar.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = e.target instanceof HTMLElement && (e.target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName));
      const slash = e.key === '/' && !typing && !e.metaKey && !e.ctrlKey && !e.altKey;
      const ctrlK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (!slash && !ctrlK) return;
      if (!window.matchMedia('(min-width: 640px)').matches) return;
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
      setOpen(true);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // A click anywhere else closes the suggestions.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => setPicked(false), [s.debounced]);

  const done = () => {
    setOpen(false);
    setPicked(false);
    inputRef.current?.blur();
  };
  const pick = (r: SearchResult) => {
    done();
    s.reset();
    if (r.orgId !== activeOrgId) switchOrg(r.orgId, r.href);
    else navigate(r.href);
  };
  const seeAll = () => {
    const q = s.q.trim();
    if (q.length < 2) return;
    done();
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      done();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      if (!picked) {
        setPicked(true);
        s.setHighlight(0);
      } else {
        s.setHighlight((h) => Math.min(s.flat.length - 1, h + 1));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (picked && s.highlight === 0) setPicked(false);
      else s.setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = picked ? s.flat[s.highlight] : undefined;
      if (target) pick(target);
      else seeAll();
    }
  };

  const showing = open && s.q.trim().length > 0;

  return (
    <div ref={boxRef} className="relative w-full max-w-2xl" data-testid="search-bar">
      <div
        className={cn(
          // The focus ring is the bar's, round like it, not the input's square one inside.
          'flex items-center gap-2 rounded-full border border-input bg-background px-4 shadow-sm transition-shadow focus-within:border-primary focus-within:shadow-md focus-within:ring-2 focus-within:ring-ring/30',
          showing && 'rounded-b-none rounded-t-2xl border-b-transparent shadow-md',
        )}
      >
        <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-expanded={showing}
          aria-controls="search-bar-suggestions"
          aria-label="Search"
          value={s.q}
          onChange={(e) => {
            s.setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search invoices, customers, expenses, bank lines… (press /)"
          data-testid="search-bar-input"
          className="h-10 min-w-0 flex-1 bg-transparent text-sm outline-none focus-visible:ring-0 focus-visible:ring-offset-0 [&::-webkit-search-cancel-button]:hidden"
        />
        {s.q ? (
          <button
            type="button"
            onClick={() => {
              s.reset();
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
            className="flex size-7 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-accent"
          >
            <X className="size-4" aria-hidden />
          </button>
        ) : (
          <kbd className="hidden shrink-0 rounded border border-border px-1.5 text-[10px] font-medium text-muted-foreground md:inline">/</kbd>
        )}
      </div>
      {showing ? (
        <div
          id="search-bar-suggestions"
          data-testid="search-bar-suggestions"
          className="absolute inset-x-0 top-full z-40 max-h-[70vh] overflow-y-auto overscroll-contain rounded-b-2xl border border-t-0 border-border bg-background shadow-lg"
        >
          <SearchResultsList
            groups={s.groups}
            flat={s.flat}
            highlight={picked ? s.highlight : -1}
            onPick={pick}
            query={s.q}
            debounced={s.debounced}
            isFetching={s.isFetching}
            manyBooks={s.manyBooks}
            amount={s.data?.amount ? Number(s.data.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : null}
          />
          {s.q.trim().length >= 2 ? (
            <button
              type="button"
              onClick={seeAll}
              data-testid="search-see-all"
              className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm text-primary hover:bg-accent"
            >
              <Search className="size-4" aria-hidden />
              See all results for “{s.q.trim()}”
              <span className="ml-auto text-[10px] text-muted-foreground">Enter</span>
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
